import { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Link } from "react-router-dom";
import { GetUserBusinessUnits } from "app/features/userSlice";
import { getBusinesses } from "app/features/businessesSlice";
import { LocalStorageHelper } from "lib/helpers/localStorageHelper";
import { getPersonsByCompany } from "app/features/personSlice";
import { DataGrid, gridClasses, GridToolbar, GridSelectionModel } from "@mui/x-data-grid";
import { alpha } from "@mui/material/styles";
import { styled } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { dataGridMapper, personSurveySendMapper, getPersonsByIdsArray } from "lib/helpers/surveyHelper";
import { selectBusiness } from "app/features/businessesSlice";
import { PersonSurveySend, PersonXLS } from "types";
import {
  setError,
  setSuccess,
  sendSMSSurvey,
  sendEmailSurvey,
  hideSurveyToast,
  clearSurveyError,
  clearSurveyResponse,
} from "app/features/surveySlice";
import LinearProgress from "@mui/material/LinearProgress";
import NoDataOverlay from "./components/NoDataOverlay";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import ActionList from "./components/ActionList";
import { getToastMessage } from "lib/helpers/toastMessageHelper";
import { personService } from "services/PersonService";
import { AppSettings } from "appSettings/appSettings.dev";
import NewToast from "ui/atoms/NewToast";
import withAuth from "lib/hoc/withAuth";
import Modal from "ui/organisms/Modal";
import getCurrentUser from "lib/auth/pca";
import { read, utils } from "xlsx";
import LoadFile from "./components/LoadFile";
import ListItemText from "@mui/material/ListItemText";
import { exportToXLSX } from "lib/helpers/convertToXLS";
import { fileColumns } from "./constants/columns";
import Filters from "./components/Filters";
import Columns from "./components/Columns";
import Chip from "ui/molecules/Chip";

const UsersSurvey = () => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const surveyURL = AppSettings?.msal.redirectUri || "https://portal-gr-ver2-test.azurewebsites.net";
  const { userBusinesses } = useAppSelector((state) => state.user) ?? [];
  const { persons, personLoading } = useAppSelector((state) => state.person);
  const { businessSelected } = useAppSelector((state) => state.businesses);
  const { menu } = useAppSelector((state) => state.currentMenu) ?? [];

  const { surveyError, surveyLoading, surveyResponse, surveySeverity, surveyShowToast } = useAppSelector(
    (state) => state.survey
  );

  const [open, setOpen] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [toastDuration, setToastDuration] = useState<number>(4000);
  const [isFileSaving, setIsFileSaving] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("Nombre del archivo");
  const [jsonData, setJsonData] = useState<PersonXLS[]>([]);
  const [business, setBusiness] = useState<any>(businessSelected);
  const [filterValue, setFilterValue] = useState<string>("ALL");
  const [active, setActive] = useState<string>("true");
  const [vinculationType, setVinculationType] = useState<string>("ALL");
  const [personSelected, setPersonSelected] = useState<PersonSurveySend>();
  const userLogged = LocalStorageHelper.getItem("user-logged");
  const isButtonDisabled = !personSelected?.listTo?.length || surveyLoading;

  const onSelectTableRow = (businessIds: GridSelectionModel) => {
    const businessFromPersonArray = getPersonsByIdsArray(persons, businessIds);
    const payload = personSurveySendMapper(businessFromPersonArray, surveyURL);
    setPersonSelected(payload);
  };

  const onSelectBusiness = (event: SelectChangeEvent) => {
    const params = {
      codeUp: event.target.value,
      filter: filterValue,
      active,
      vinculationType,
    };
    dispatch(getPersonsByCompany(params));
    dispatch(selectBusiness(params));
    setBusiness(params);
  };

  const onSendEmailClick = async () => {
    await dispatch(sendEmailSurvey(personSelected as PersonSurveySend));
  };

  const onSendSMSClick = async () => {
    await dispatch(sendSMSSurvey(personSelected as PersonSurveySend));
  };

  const savePersonFromXLS = () => {
    setIsFileSaving(true);
    const currentUser = getCurrentUser();
    if (jsonData?.length) {
      personService
        .savePersonXLS(jsonData, currentUser, fileName)
        .then((response: any) => {
          setIsFileSaving(false);
          setToastDuration(4000);
          dispatch(setSuccess(response));
          restoreFileSettings();
          dispatch(getPersonsByCompany({ ...business, filter: filterValue }));
          setOpen(false);
        })
        .catch((err: any) => {
          let errorMessage = "";
          if (Array.isArray(err?.data)) {
            errorMessage = err?.data?.join("$$");
          } else {
            errorMessage = err?.data || "Error al guardar los datos o error en el archivo.";
          }
          setToastDuration(10000);
          dispatch(setError({ message: errorMessage, severity: "warning" }));
          setIsFileSaving(false);
        });
    }
  };

  const clearToast = () => {
    dispatch(hideSurveyToast());
    dispatch(clearSurveyResponse());
    dispatch(clearSurveyError());
  };

  useEffect(() => {
    dispatch(getBusinesses());
    if (isAuthenticated) {
      const userLogged = accounts[0]?.username;
      dispatch(GetUserBusinessUnits(userLogged));
    }

    return () => {
      clearToast();
    };
  }, [userLogged]);

  useEffect(() => {
    dispatch(getPersonsByCompany({ ...business, filter: filterValue, active, vinculationType }));
  }, [filterValue, active, vinculationType]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const maxFileSize = 5242880;
    if (event.target.files) {
      const file = event.target.files[0];
      if (file.size < maxFileSize) {
        const text = file?.name?.length > 45 ? `${file.name?.substring(0, 45)}...` : file.name;
        setFileName(text);
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const data = e.target.result;
          const workbook = read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          // Exclude new lines from column headers
          const columnHeaders = Object.keys(worksheet)
            .filter((key) => key[0] !== "!" && worksheet[key].t === "s" && typeof worksheet[key].v === "string")
            .map((key) => worksheet[key].v.replace(/\r/g, "").split("\n")[0]);
          const rows = utils.sheet_to_json(worksheet, { header: columnHeaders });

          // Convert the value to M/D/YYYY"
          const formattedRows: PersonXLS[] = rows.map((row: any) => {
            const formattedRow: PersonXLS = {
              Orden: row.Orden,
              TipoDocumento: row.TipoDocumento,
              NumeroDocumento: row.NumeroDocumento,
              Nombres: row.Nombres,
              Apellidos: row.Apellidos,
              EMAIL: row.EMAIL,
              Celular: row.Celular,
              CajaCompensacion: row.CajaCompensacion,
              FondoPensiones: row.FondoPensiones,
              Cesantias: row.Cesantias,
              EPS: row.EPS,
              Sede: row.Sede,
              Cargo: row.Cargo,
              TipoContrato: row.TipoContrato,
              FechaInicio: row.FechaInicio,
              FechaFin: row.FechaFin,
              EmpresaTemporal: row.EmpresaTemporal,
            };
            if (formattedRow.FechaInicio && typeof formattedRow.FechaInicio === "number") {
              const dateValue = dayjs("1/1/1900").add(formattedRow.FechaInicio - 2, "day");
              const formattedDate = dateValue.format("M/D/YYYY");
              formattedRow.FechaInicio = formattedDate;
            }
            return formattedRow;
          });

          const newRows = [...formattedRows]; // Copy to avoid mutation
          newRows.shift(); // Delete the first element; it's the column headers
          setJsonData(newRows); // Update the state
        };
        reader.readAsArrayBuffer(file);
      } else {
        dispatch(
          setError({
            message: "El archivo que intenta cargar es demasiado grande. El tamaño permitido es de 5MB ",
            severity: "warning",
          })
        );
      }
    }
  };

  const handleDownload = () => {
    const url = process.env.PUBLIC_URL + "/files/Plantilla.xls";
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const fileName = "Plantilla.xls";
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      });
  };

  function restoreFileSettings() {
    setJsonData([]);
    setFileName("Nombre del archivo");
  }

  const onModalClose = () => {
    restoreFileSettings();
    clearToast();
  };

  function handleExportFile() {
    setLoadingFile(true);
    setTimeout(() => {
      const data = dataGridMapper(persons);
      const filename = "Report.xlsx";
      exportToXLSX(data, fileColumns, filename);
      setLoadingFile(false);
    }, 1000);
  }

  return (
    <>
      <NewToast open={surveyShowToast} setOpen={clearToast} severity={surveySeverity} autoHideDuration={toastDuration}>
        {getToastMessage(surveyResponse, surveyError)}
      </NewToast>

      <ContainerStyled maxWidth='xl'>
        <div style={{ height: 800, width: "100%" }}>
          <StackStyled>
            <ActionList
              menu={menu}
              setOpen={setOpen}
              handleDownload={handleDownload}
              onSendSMSClick={onSendSMSClick}
              onSendEmailClick={onSendEmailClick}
              isButtonDisabled={isButtonDisabled as boolean}
            />
          </StackStyled>

          <Filters
            business={business}
            onSelectBusiness={onSelectBusiness}
            userBusinesses={userBusinesses}
            active={active}
            filterValue={filterValue}
            vinculationType={vinculationType}
            setActive={setActive}
            setVinculationType={setVinculationType}
            setFilterValue={setFilterValue}
            loadingFile={loadingFile as boolean}
            handleExportFile={handleExportFile}
            persons={persons}
          />

          <Chip
            link='/survey/file-history'
            label='Historial Archivos'
            color='primary'
            backgroundColor='#4e5559'
            hoverTextColor='white'
            hoverBackgroundColor='#727b81'
          />

          <StripedDataGrid
            checkboxSelection
            columns={Columns}
            loading={personLoading as boolean}
            rows={dataGridMapper(persons) || []}
            onSelectionModelChange={onSelectTableRow}
            components={{
              Toolbar: GridToolbar,
              LoadingOverlay: LinearProgress,
              NoRowsOverlay: NoDataOverlay,
            }}
            componentsProps={{
              toolbar: {
                printOptions: {
                  hidden: true,
                },
              },
            }}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")}
          />
        </div>
      </ContainerStyled>
      <Modal open={open} setOpen={setOpen} displayCancelAction onCloseCallback={onModalClose}>
        <LoadFile
          fileName={fileName}
          isFileSaving={isFileSaving}
          disabled={!jsonData?.length}
          handleFileUpload={handleFileUpload}
          savePersonFromXLS={savePersonFromXLS}
        />
      </Modal>
    </>
  );
};

export default withAuth(UsersSurvey);

const ODD_OPACITY = 0.2;

const ContainerStyled = styled(Container)`
  background-color: ${({ theme }) => theme.palette.background.default};
  height: 1150px;
  padding: 1.7rem;
  margin: "auto";
`;

const StackStyled = styled(Stack)`
  display: flex;
  padding: 8px 0;
  flex-direction: row;
  justify-content: right;
  @media (max-width: 1190px) {
    flex-direction: column;
  }
`;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  ".css-1rcsupe-MuiButtonBase-root-MuiCheckbox-root": {
    color: `${theme.palette.text.primary} !important`,
  },
  "&.MuiDataGrid-root": {
    height: "90%",
    "@media (max-width: 570px)": {
      height: "60%",
    },
    "@media screen and (min-width: 570px) and (max-width:900px)": {
      height: "70%",
    },
  },
  [`& .${gridClasses.row}.even`]: {
    //backgroundColor: theme.palette.grey[200],
    backgroundColor: "#e9ebec",
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      //backgroundColor: "#c3e5fa",
      "&:hover, &.Mui-hovered": {
        //backgroundColor: "#f5eaea",
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          //backgroundColor: "#a2eec3",
        },
      },
    },
  },
}));
